import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EnergyLabelService } from '@cfp/shared/energy-label';
import { RoadmapPortfolioDetails } from '@cfp/shared/roadmap';
import { ChartAbstract } from '@cfp/shared/ui/chart';
import { Card, ChartDataTableSidePanelData, Icon, Link, LiveRegionStatusUpdaterModule } from '@cfp/ui';
import { lightenDarkenColor } from '@cfp/ui/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartDataset, ChartType } from 'chart.js';
import {
  IndexTypeDTO,
  LabelGroupDTO,
  RoadmapBuildingDetailsDTO,
  RoadmapBuildingYearDTO,
  RoadmapPortfolioDetailsDisplayLabelsYearDTO
} from 'csr-asset-app';
import { BaseChartDirective } from 'ng2-charts';
import { ChartOptionsWithMissingProperties } from '../roadmap-chart/chart-options-with-missing-properties';

interface LabelAndColor {
  label: string;
  color: string;
}
interface GroupedIndexType {
  labelGroup: LabelGroupDTO;
  indexTypes: IndexTypeDTO[];
}

@Component({
  selector: 'cfp-roadmap-chart-labels',
  templateUrl: './roadmap-chart-labels.html',
  imports: [Card, TranslateModule, FormsModule, LiveRegionStatusUpdaterModule, Icon, BaseChartDirective, Link],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoadmapChartLabels<
  T extends RoadmapBuildingDetailsDTO | RoadmapPortfolioDetails
> extends ChartAbstract<T> {
  public readonly labelGroupFromInput = input<LabelGroupDTO>(undefined, { alias: 'labelGroup' });
  public readonly totals = input<
    | {
        [key: string]: RoadmapPortfolioDetailsDisplayLabelsYearDTO;
      }
    | undefined
  >(undefined);

  protected readonly chartType: ChartType = 'bar';
  protected readonly chartData = computed(() => {
    const labelGroup = this.labelGroup();
    const groupedIndexType = this.groupedIndexType();
    const details = this.details();
    const totals = this.totals();
    if (!labelGroup || !groupedIndexType || !details || !totals) {
      return undefined;
    }
    let labels: LabelAndColor[] = this.energyLabelService.getEnergyLabels(labelGroup).map((label) => {
      return {
        label: label,
        color: this.energyLabelService.getColor(labelGroup, label)
      };
    });

    const datasets: ChartDataset[] = labels.map((label: any) => {
      return {
        label: this.energyLabelService.getTranslatedLabel(labelGroup, label.label),
        backgroundColor: label.color,
        borderColor: label.color,
        hoverBackgroundColor: lightenDarkenColor(label.color, -20),
        hoverBorderColor: lightenDarkenColor(label.color, -40),
        borderWidth: this.lineWidth,
        hoverBorderWidth: 2,
        barPercentage: 0.6,
        data: details.years
          .filter((year: RoadmapBuildingYearDTO) => year.year)
          .map((year: RoadmapBuildingYearDTO) => {
            if (!year.year || !totals[year.year]) {
              return 0;
            }

            const totalsForIndexType =
              totals[year.year].perLabelGroup[labelGroup]?.filter((total) => {
                return groupedIndexType.indexTypes.includes(total.label.indexType);
              }) || [];
            let total: number = 0;
            totalsForIndexType.forEach((totalForIndexType) => {
              if (totalForIndexType.label.label === label.label) {
                total += totalForIndexType.total;
              }
            });
            return total;
          }),
        pointStyle: 'line',
        pointBorderWidth: 2,
        pointBorderColor: label.color,
        cubicInterpolationMode: 'monotone',
        borderRadius: this.borderRadius
      };
    });

    return {
      labels: details.years
        .filter((year: RoadmapBuildingYearDTO) => year.year)
        .map((year: RoadmapBuildingYearDTO) => {
          return year.year;
        }),
      datasets: datasets.filter((dataset) => dataset.data.some((total: number) => total > 0))
    };
  });

  protected readonly chartOptions: ChartOptionsWithMissingProperties = {
    responsive: true,
    resizeDelay: 200,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false
        },
        ticks: {
          autoSkip: false
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          display: true
        },
        ticks: {
          callback: (value: number) => {
            if (value % 1 === 0) {
              return value;
            }

            return undefined;
          }
        }
      }
    },
    maintainAspectRatio: false,
    elements: {
      point: {
        radius: 0
      }
    },
    plugins: {
      datalabels: { display: () => false },
      legend: {
        display: true,
        position: 'bottom',
        align: 'start',
        labels: {
          usePointStyle: false,
          padding: 16,
          boxWidth: 12
        }
      }
    }
  } as any;

  protected readonly groupedIndexTypes = computed(() => {
    const totals = this.totals();
    if (!totals) {
      return undefined;
    }

    const allLabelGroups = Object.values(LabelGroupDTO);

    const groupedIndexTypes = allLabelGroups
      .map((labelGroup) => {
        const indexTypes = [
          ...new Set(
            Object.keys(totals)
              .filter((year: string) => year !== 'default')
              .map((key) => {
                return totals[key].perLabelGroup[labelGroup]?.map((total) => total.label?.indexType);
              })
              .flat()
          )
        ];

        return {
          labelGroup: labelGroup,
          indexTypes: indexTypes
        };
      })
      .filter((group) => group.indexTypes.length > 0);
    return groupedIndexTypes;
  });

  protected readonly selectedGroupedIndexType = model<GroupedIndexType>();
  protected readonly groupedIndexType = computed(() => {
    if (this.selectedGroupedIndexType()) {
      return this.selectedGroupedIndexType();
    }
    const labelGroup = this.labelGroup();
    const groupedIndexTypes = this.groupedIndexTypes();
    if (labelGroup && groupedIndexTypes) {
      return groupedIndexTypes.find((groupedIndexType) => groupedIndexType.labelGroup === labelGroup);
    }
    return groupedIndexTypes && groupedIndexTypes.length > 0 ? groupedIndexTypes[0] : undefined;
  });

  protected readonly labelGroup = computed(() => this.labelGroupFromInput() || this.labelGroupFromGroupedIndexType());

  private readonly lineWidth: number = 0;
  private readonly borderRadius: number;
  private readonly labelGroupFromGroupedIndexType = signal<LabelGroupDTO | undefined>(undefined);

  private readonly translateService = inject(TranslateService);
  private readonly energyLabelService = inject(EnergyLabelService);

  public constructor() {
    super();

    const style = getComputedStyle(document.body);
    const chartRadius = style.getPropertyValue('--chart-radius').trim();
    this.borderRadius = chartRadius ? +chartRadius : 3;

    if (this.chartOptions?.plugins?.legend?.labels) {
      this.chartOptions.plugins.legend.labels.useBorderRadius = this.borderRadius > 0;
      this.chartOptions.plugins.legend.labels.borderRadius = this.borderRadius;
    }

    effect(() => {
      const groupedIndexType = this.groupedIndexType();
      if (groupedIndexType) {
        this.labelGroupFromGroupedIndexType.set(groupedIndexType.labelGroup);
      }
    });
  }

  public setGroupedIndexType(groupedIndexType: GroupedIndexType): void {
    this.selectedGroupedIndexType.set(groupedIndexType);
  }

  public getTranslation(key: string): string | undefined {
    const text = this.translateService.instant(key);
    if (text !== key) {
      return text;
    }

    return undefined;
  }

  private transformToTableData(): ChartDataTableSidePanelData | undefined {
    const chartData = this.chartData();
    if (!chartData) {
      return undefined;
    }

    const columns: string[] = [this.translateService.instant('year')];

    chartData.datasets.forEach((dataSet: ChartDataset) => {
      columns.push(dataSet.label!);
    });

    const data: string[][] = chartData.labels.map((year: any, yearIndex: number) => {
      const rowData: string[] = [year];
      chartData.datasets.forEach((dataSet: ChartDataset) => {
        if (dataSet.data && dataSet.data[yearIndex]) {
          rowData.push(dataSet.data[yearIndex].toString());
        }
      });
      return rowData;
    });

    return {
      caption: this.translateService.instant('roadmap.labelDistribution'),
      columns: columns,
      data: data
    };
  }

  public showData(): void {
    const tableData = this.transformToTableData();
    if (!tableData) {
      return;
    }
    this.showDataEvent.emit(tableData);
  }
}
